import React from "react";
import ReactDOM from "react-dom";
import { FiCheckCircle, FiKey, FiX } from "react-icons/fi";
import { retrieveOpenVendingToken } from "../../../api-calls/guest/openVending";
import { CopyButton, Field, Notice, PrimaryButton, groupToken, naira } from "../../landing/ui";

const unwrap = (res) => res?.data ?? res;
const errorText = (e) => e?.response?.data?.message || e?.message || "Something went wrong";

const findToken = (data) => {
  const t = data?.rechargeData?.token || data?.token || data?.meterToken || data?.electricityToken;
  return t ? String(t) : "";
};

export default function RetrieveTokenModal({ initialMeterId = "", onClose }) {
  const [form, setForm] = React.useState({ meterId: initialMeterId, accountNumber: "" });
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null); // { message, isError, data, token }

  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: "" }));
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.meterId.trim()) next.meterId = "Enter your meter number";
    if (!form.accountNumber.trim()) next.accountNumber = "Enter the account number you paid to";
    setErrors(next);
    if (Object.keys(next).length) return;
    setLoading(true);
    setResult(null);
    retrieveOpenVendingToken({ meterId: form.meterId.trim(), accountNumber: form.accountNumber.trim() })
      .then((res) => {
        const data = unwrap(res);
        setResult({ data, token: findToken(data), message: res?.message || data?.message, isError: false });
      })
      .catch((err) => setResult({ message: errorText(err), isError: true }))
      .finally(() => setLoading(false));
  };

  const token = result?.token;
  const data = result?.data && !Array.isArray(result.data) ? result.data : {};

  const modal = (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center bg-[#010623]/60 backdrop-blur-sm sm:items-center sm:p-6"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <style>{`@keyframes ovUp{from{transform:translateY(24px);opacity:0}to{transform:none;opacity:1}}`}</style>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Retrieve token"
        style={{ animation: "ovUp .25s ease-out" }}
        className="flex max-h-[94vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:max-w-[440px] sm:rounded-[28px]"
      >
        <div className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-dark text-secondary-dark">
              <FiKey />
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-bold text-primary-dark">Retrieve token</p>
              <p className="truncate text-xs text-[#6C6F7F]">Already paid? Get your token back</p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F3F3F7] text-primary-dark hover:bg-[#E6E6EC]"
          >
            <FiX />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label="Meter number" inputMode="numeric" placeholder="e.g. 7126352072"
              value={form.meterId} onChange={setField("meterId")} error={errors.meterId} disabled={loading} />
            <Field label="Account number you paid to" inputMode="numeric" placeholder="The account number shown at checkout"
              value={form.accountNumber} onChange={setField("accountNumber")} error={errors.accountNumber} disabled={loading} />

            {result?.message && (
              <Notice tone={result.isError ? "red" : token ? "green" : "amber"}>{result.message}</Notice>
            )}

            {token && (
              <>
                <div className="flex flex-col items-center gap-1 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl text-green-600">
                    <FiCheckCircle />
                  </span>
                </div>
                <div className="rounded-2xl bg-primary-dark p-5 text-center text-white">
                  <p className="text-xs font-semibold uppercase tracking-wider text-white/60">Electricity token</p>
                  <p className="mt-2 break-words text-[26px] font-extrabold leading-tight tracking-[0.12em]">
                    {groupToken(token)}
                  </p>
                  <div className="mt-4 flex justify-center">
                    <CopyButton value={token.replace(/\D/g, "")} label="Copy token" />
                  </div>
                </div>
                {(data.rechargeData?.unit != null || data.tokenAmount != null) && (
                  <div className="grid grid-cols-2 gap-3 rounded-2xl bg-[#F3F3F7] p-4 text-sm">
                    {data.rechargeData?.unit != null && (
                      <div><p className="text-[#6C6F7F]">Units</p><p className="font-bold">{data.rechargeData.unit} kWh</p></div>
                    )}
                    {data.tokenAmount != null && (
                      <div><p className="text-[#6C6F7F]">Amount</p><p className="font-bold">{naira(data.tokenAmount)}</p></div>
                    )}
                  </div>
                )}
              </>
            )}

            {token ? (
              <PrimaryButton onClick={onClose}>Done</PrimaryButton>
            ) : (
              <PrimaryButton type="submit" loading={loading} disabled={loading}>
                Retrieve token
              </PrimaryButton>
            )}
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.getElementById("modal") || document.body);
}
